import React, { useState } from 'react';

import { Typography } from '@mui/material';
import type { GridCellParams } from '@mui/x-data-grid-premium';
import { PICKER_CHIP_ICON_SIZE, usePickerChipStyles } from 'src/components/generic';
import { LucideIconRenderer } from 'src/components/generic/LucideIconRenderer';
import { BoardList } from 'src/components/workflow/board/board-toolbar/BoardFilterPicker';
import type { WorkflowListGridRow } from 'src/components/workflow/workflow-list';
import {
  getIsWorkflowListGroupRow,
  getIsWorkflowListWorkflowRow,
  getWorkflowIdFromListRow,
} from 'src/components/workflow/workflow-list';
import { WorkflowListGroupLabelCell } from 'src/components/workflow/workflow-list/cells/WorkflowListGroupLabelCell';
import { WorkflowBoardRelocationDialog } from 'src/components/workflow/WorkflowBoardRelocationDialog';
import { Apollo } from 'src/gql/generated';

export function WorkflowListBoardCell({
  field,
  row,
}: GridCellParams<WorkflowListGridRow>): JSX.Element | null {
  const { getPickerChipClassName, pickerChipIconClassName, pickerChipLabelClassName } =
    usePickerChipStyles();
  const { data: boardsData } = Apollo.useGetBoardsQuery();
  const [relocationDialog, setRelocationDialog] = useState<{
    open: boolean;
    targetBoardId: string | null;
  }>({ open: false, targetBoardId: null });

  if (getIsWorkflowListGroupRow(row)) {
    return (
      <WorkflowListGroupLabelCell count={row.workflowCount} field={field} label={row.groupLabel} />
    );
  }
  if (!getIsWorkflowListWorkflowRow(row)) return null;

  const currentBoardId = boardsData?.boards?.find(board =>
    board.columns?.some(column => column.id === row.column.id)
  )?.id;

  return (
    <>
      <BoardList
        boards={boardsData?.boards ?? []}
        renderTrigger={(currentBoard, onClick, isOpen) => (
          <div
            className={getPickerChipClassName(isOpen)}
            onClick={event => {
              event.stopPropagation();
              onClick?.(event);
            }}
          >
            {currentBoard && (
              <div className={pickerChipIconClassName}>
                <LucideIconRenderer
                  color={currentBoard.color}
                  icon={currentBoard.icon}
                  size={PICKER_CHIP_ICON_SIZE}
                />
              </div>
            )}
            <Typography noWrap className={pickerChipLabelClassName} variant="body2">
              {currentBoard?.name || 'No Board'}
            </Typography>
          </div>
        )}
        selectedBoardId={currentBoardId}
        onBoardChange={targetBoardId => setRelocationDialog({ open: true, targetBoardId })}
      />
      {relocationDialog.open && relocationDialog.targetBoardId && (
        <WorkflowBoardRelocationDialog
          currentBoardId={currentBoardId ?? null}
          currentColumnId={row.column.id}
          currentDoneColumnId={row.doneColumn?.id}
          open={relocationDialog.open}
          phases={row.phases ?? []}
          startedAt={row.startedAt}
          targetBoardId={relocationDialog.targetBoardId}
          workflowId={getWorkflowIdFromListRow(row)}
          workflowName={row.name}
          onClose={() => setRelocationDialog({ open: false, targetBoardId: null })}
        />
      )}
    </>
  );
}
